import React, { useState, useMemo } from "react";
import { Card, CardBody, Input } from "reactstrap";

const CustomDataTable = ({ tableData }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return tableData.rows;

    return tableData.rows.filter((row) => {
      return tableData.columns.some((col) => {
        const value = row[col.field];
        return (
          value &&
          value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    });
  }, [tableData.rows, searchTerm, tableData.columns]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortField) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aVal = a[sortField] || "";
      const bVal = b[sortField] || "";

      if (sortDirection === "asc") {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }, [filteredData, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = sortedData.slice(startIndex, startIndex + rowsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return "";
    return sortDirection === "asc" ? " ↑" : " ↓";
  };

  return (
    <Card>
      <CardBody>
        {/* Search and Info */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <span className="text-muted">
              Mostrando {startIndex + 1}-
              {Math.min(startIndex + rowsPerPage, sortedData.length)} de{" "}
              {sortedData.length} elementos
            </span>
          </div>
          <Input
            type="text"
            placeholder="Buscar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: "200px" }}
          />
        </div>

        {/* Table */}
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                {tableData.columns.map((col) => (
                  <th
                    key={col.field}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort(col.field)}
                  >
                    {col.label}
                    {getSortIcon(col.field)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={tableData.columns.length}
                    className="text-center"
                  >
                    No existen elementos
                  </td>
                </tr>
              ) : (
                paginatedData.map((row, index) => (
                  <tr key={index}>
                    {tableData.columns.map((col) => (
                      <td key={col.field}>{row[col.field] || "N/A"}</td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div>
              <span className="text-muted">
                Página {currentPage} de {totalPages}
              </span>
            </div>
            <div>
              <button
                className="btn btn-sm btn-outline-primary mr-2"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Atras
              </button>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default CustomDataTable;
